// ApiError — the single error type every layer above `api/` may catch (contract 03 §5,
// ledger A2). Raw AxiosError never escapes this module. Services branch on `kind`/`code`,
// NEVER on English message substrings. Every ApiError carries an i18nKey.
import { isAxiosError, isCancel } from 'axios'

export type ApiErrorKind =
  | 'network' // no response: offline, DNS, CORS
  | 'timeout' // ECONNABORTED / AbortSignal timeout
  | 'canceled' // navigation / logout / unmount abort — swallowed, never toasted
  | 'unauthorized' // 401
  | 'paymentRequired' // 402 (credit depletion / package gate)
  | 'forbidden' // 403
  | 'notFound' // 404
  | 'validation' // 400 / 422 with field errors
  | 'conflict' // 409
  | 'rateLimited' // 429
  | 'server' // 5xx
  | 'unknown'

interface ApiErrorProps {
  kind: ApiErrorKind
  status: number | null
  code: string | null
  fields: Record<string, string[]>
  payload: unknown
  userVisible: boolean
  detail: string
  retryAfterMs?: number | null
}

const STATUS_KIND: Partial<Record<number, ApiErrorKind>> = {
  400: 'validation',
  401: 'unauthorized',
  402: 'paymentRequired',
  403: 'forbidden',
  404: 'notFound',
  409: 'conflict',
  422: 'validation',
  429: 'rateLimited',
}

function kindFromStatus(status: number): ApiErrorKind {
  const mapped = STATUS_KIND[status]
  if (mapped) return mapped
  if (status >= 500) return 'server'
  return 'unknown'
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** Backend machine code: `body.error.code` (auth envelope) → `body.code` → `body.error_code`. */
function extractCode(body: unknown): string | null {
  const b = asRecord(body)
  if (!b) return null
  const err = asRecord(b.error)
  if (err && typeof err.code === 'string') return err.code
  if (typeof b.code === 'string') return b.code
  if (typeof b.error_code === 'string') return b.error_code
  return null
}

/** DRF-style field errors: `body.error.fields` (auth envelope) → `body.errors`. */
function extractFields(body: unknown): Record<string, string[]> {
  const b = asRecord(body)
  if (!b) return {}
  const err = asRecord(b.error)
  // Scorecards' DRF adapter carries INVALID_REQUEST's field map under `error.details`.
  // Only treat that key as fields for the explicit validation code: other endpoints use
  // `details` for diagnostic metadata (partition ids, retry hints, etc.).
  const invalidRequestDetails = err?.code === 'INVALID_REQUEST' ? asRecord(err.details) : null
  const source = asRecord(err?.fields) ?? asRecord(b.errors) ?? invalidRequestDetails
  if (!source) return {}
  const out: Record<string, string[]> = {}
  for (const [key, value] of Object.entries(source)) {
    if (Array.isArray(value)) {
      const msgs = value.filter((v): v is string => typeof v === 'string')
      if (msgs.length > 0) out[key] = msgs
    } else if (typeof value === 'string') {
      out[key] = [value]
    }
  }
  return out
}

/**
 * `Retry-After` in ms — delta-seconds or an HTTP-date. Null when absent or unreadable.
 *
 * Read here rather than in `retry.ts` because the header outlives the retry decision: a 429 on a
 * POST is never replayed, and the feature that catches it still has to tell the user how long to
 * wait. `retry.ts` reads this same value off the ApiError, so there is one parser.
 */
function extractRetryAfterMs(headers: unknown, now: number): number | null {
  if (!headers || typeof headers !== 'object') return null

  // AxiosHeaders stores a header under the name the server sent (`Retry-After`), not a
  // lowercased one — only its `get` normalizes. Plain objects get a case-insensitive scan.
  const bag = headers as { get?: (name: string) => unknown } & Record<string, unknown>
  const raw =
    typeof bag.get === 'function'
      ? bag.get('retry-after')
      : Object.entries(bag).find(([name]) => name.toLowerCase() === 'retry-after')?.[1]

  if (typeof raw !== 'string' && typeof raw !== 'number') return null
  const text = String(raw).trim()
  if (text === '') return null

  const seconds = Number(text)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000)

  const at = Date.parse(text)
  return Number.isNaN(at) ? null : Math.max(0, at - now)
}

/** Backend display-policy signal (`body.error.user_visible`); defaults to true when absent. */
function extractUserVisible(body: unknown): boolean {
  const err = asRecord(asRecord(body)?.error)
  if (err && typeof err.user_visible === 'boolean') return err.user_visible
  return true
}

/** Human-joined message from the body (error.message / message / detail + field msgs), for logs. */
function extractDetail(body: unknown, fields: Record<string, string[]>, fallback: string): string {
  const parts: string[] = []
  const b = asRecord(body)
  if (b) {
    const err = b.error
    const errRecord = asRecord(err)
    if (errRecord && typeof errRecord.message === 'string') parts.push(errRecord.message)
    else if (typeof err === 'string') parts.push(err) // controller `{ error: '...' }`
    if (typeof b.message === 'string') parts.push(b.message)
    if (typeof b.detail === 'string') parts.push(b.detail)
  } else if (typeof body === 'string') {
    parts.push(body)
  }
  for (const msgs of Object.values(fields)) parts.push(...msgs)
  const joined = [...new Set(parts.map((p) => p.trim()).filter(Boolean))].join(' ')
  return joined || fallback
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number | null
  /** Backend machine code when present, e.g. 'INVALID_CREDENTIALS', 'CREDIT_DEPLETED'. */
  readonly code: string | null
  /** DRF-style field errors: { field: [msg, ...] }. Empty object when none. */
  readonly fields: Record<string, string[]>
  /** Raw response body, for feature-specific reads. */
  readonly payload: unknown
  /** Backend display-policy signal; defaults to true when absent. */
  readonly userVisible: boolean
  /** i18n key for the global toast: `errors.api.<code>` if code known, else `errors.http.<kind>`. */
  readonly i18nKey: string
  /** Human-joined message from the body (recursive extract), for logs/fallback interpolation. */
  readonly detail: string
  readonly retryable: boolean
  /** `Retry-After` in ms when the server named one (429/503), else null. Drives the retry
   *  backoff AND the wait a feature shows after a 429 it is not allowed to replay. */
  readonly retryAfterMs: number | null

  constructor(props: ApiErrorProps) {
    super(props.detail || props.kind)
    this.name = 'ApiError'
    this.kind = props.kind
    this.status = props.status
    this.code = props.code
    this.fields = props.fields
    this.payload = props.payload
    this.userVisible = props.userVisible
    this.detail = props.detail
    this.i18nKey = props.code ? `errors.api.${props.code}` : `errors.http.${props.kind}`
    this.retryable = props.kind === 'network' || props.kind === 'timeout' || props.kind === 'server'
    this.retryAfterMs = props.retryAfterMs ?? null
    // Restore the prototype chain (transpile-to-ES5 safety for instanceof).
    Object.setPrototypeOf(this, ApiError.prototype)
  }

  static fromAxios(error: unknown): ApiError {
    // 1. Cancellation is the first branch — never toasted, never a logout trigger.
    if (isCancel(error) || isAbortLike(error)) {
      return new ApiError({
        kind: 'canceled',
        status: null,
        code: null,
        fields: {},
        payload: null,
        userVisible: false,
        detail: 'canceled',
      })
    }

    if (isAxiosError(error)) {
      const response = error.response
      const fallbackMessage = error.message || 'Request failed'
      if (!response) {
        // Transport failure — no backend body, so `code` stays null (a transport code like
        // ERR_NETWORK/ECONNABORTED is NOT a backend machine code and must not key errors.api.*).
        const kind: ApiErrorKind = error.code === 'ECONNABORTED' ? 'timeout' : 'network'
        return new ApiError({
          kind,
          status: null,
          code: null,
          fields: {},
          payload: null,
          userVisible: true,
          detail: fallbackMessage,
        })
      }
      const body = response.data
      const fields = extractFields(body)
      return new ApiError({
        kind: kindFromStatus(response.status),
        status: response.status,
        code: extractCode(body),
        fields,
        payload: body,
        userVisible: extractUserVisible(body),
        detail: extractDetail(body, fields, fallbackMessage),
        retryAfterMs: extractRetryAfterMs(response.headers, Date.now()),
      })
    }

    // Non-axios throwable (programmer error, thrown string, etc.).
    const detail = error instanceof Error ? error.message : String(error)
    return new ApiError({
      kind: 'unknown',
      status: null,
      code: null,
      fields: {},
      payload: null,
      userVisible: true,
      detail: detail || 'Unknown error',
    })
  }

  /**
   * Normalize a bare transport status — the non-axios path (`eventStream`, contract 03 §6a),
   * where the failure arrives as a polyfill event rather than an AxiosError and there is no
   * response body to mine for `code`/`fields`. `null` means no response at all → `network`.
   */
  static fromStatus(status: number | null, detail: string): ApiError {
    return new ApiError({
      kind: status === null ? 'network' : kindFromStatus(status),
      status,
      code: null,
      fields: {},
      payload: null,
      userVisible: true,
      detail,
    })
  }

  static isApiError(e: unknown): e is ApiError {
    return e instanceof ApiError
  }
}

/** AbortController/`AbortSignal` timeout rejections that axios surfaces outside isCancel. */
function isAbortLike(error: unknown): boolean {
  const record = asRecord(error)
  if (!record) return false
  return (
    record.name === 'AbortError' ||
    record.name === 'CanceledError' ||
    record.code === 'ERR_CANCELED'
  )
}
